import { abrePDF } from "../../../utils/pdf";
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  ButtonGroup,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";

/**
 * Control mensual de viáticos por vendedor.
 *
 * Cómo opera el negocio: al vendedor se le entrega un viático quincenal y a
 * lo largo del mes puede pedir extras. Al cierre del mes comprueba gastos y
 * se hace el balance contra todo lo que se le dio. Ese balance NO se liquida
 * aparte: se arrastra al mes siguiente para dar de más o de menos en las
 * quincenas.
 *
 * De dónde salen los números:
 *   Entregado   = Comisiones.viaticos del mes (el pago quincenal y los extras)
 *   Comprobado  = ViaticosComprobaciones del mes (lo que justificó)
 *   Diferencia  = Entregado − Comprobado
 *                   > 0  se le dio de más → lo debe
 *                   < 0  gastó de más     → se le debe
 *   Saldo       = saldo del mes anterior + diferencia del mes
 */
function ControlViaticos() {
  const { user } = useContext(AuthContext);
  const URL_COLABORADORES = process.env.REACT_APP_URL_COLABORADORES;
  const URL_VIATICOS = process.env.REACT_APP_URL_VIATICOS_COMPROBACIONES;
  const URL_VIATICOS_RESUMEN = process.env.REACT_APP_URL_VIATICOS_RESUMEN;

  const [colaboradores, setColaboradores] = useState([]);
  const [selectedColaborador, setSelectedColaborador] = useState("");
  const [periodo, setPeriodo] = useState(moment().format("YYYY-MM"));

  const [resumen, setResumen] = useState(null);
  const [comprobaciones, setComprobaciones] = useState([]);
  const [cargando, setCargando] = useState(false);

  // Alta de comprobación
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [importe, setImporte] = useState("");
  const [concepto, setConcepto] = useState("");

  const headers = {
    headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
  };

  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  useEffect(() => {
    axios
      .get(URL_COLABORADORES, headers)
      .then((res) =>
        setColaboradores(
          (res.data || []).filter((c) => c.is_active === "Si" && c.vendedor === "Si")
        )
      )
      .catch((err) => console.log(err));
  }, []);

  function cargar() {
    if (!selectedColaborador || !periodo) {
      setResumen(null);
      setComprobaciones([]);
      return;
    }
    setCargando(true);
    Promise.all([
      axios.get(`${URL_VIATICOS_RESUMEN}/${selectedColaborador}/${periodo}`, headers),
      axios.get(`${URL_VIATICOS}/${selectedColaborador}/${periodo}`, headers),
    ])
      .then(([resResumen, resComp]) => {
        setResumen(resResumen.data || null);
        setComprobaciones(resComp.data || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setCargando(false));
  }

  useEffect(cargar, [selectedColaborador, periodo]);

  function guardarComprobacion() {
    const imp = Number(importe) || 0;
    if (!selectedColaborador) {
      return Swal.fire({ icon: "error", title: "Selecciona un vendedor" });
    }
    if (imp <= 0) {
      return Swal.fire({ icon: "error", title: "Captura un importe mayor a 0" });
    }
    axios
      .post(
        URL_VIATICOS,
        {
          fecha,
          // El periodo es el mes que se está revisando, no el de la fecha:
          // un gasto de fin de mes suele comprobarse ya entrado el siguiente.
          periodo,
          colaboradores: selectedColaborador,
          importe: imp,
          concepto: concepto || "NA",
        },
        headers
      )
      .then(() => {
        setImporte("");
        setConcepto("");
        cargar();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({ icon: "error", title: "No se pudo guardar" });
      });
  }

  function cancelarComprobacion(id) {
    Swal.fire({
      title: "¿Cancelar esta comprobación?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Si, cancelar",
      cancelButtonText: "No",
      confirmButtonColor: "#dc3545",
    }).then((r) => {
      if (!r.isConfirmed) return;
      axios
        .patch(`${URL_VIATICOS}Cancel/${id}`, {}, headers)
        .then(cargar)
        .catch((err) => console.log(err));
    });
  }

  const nombreColab = resumen?.colaborador
    ? `${resumen.colaborador.nombre || ""} ${resumen.colaborador.apellido || ""}`.trim()
    : "";

  // El saldo se ajusta en las dos quincenas del mes que entra.
  const ajusteQuincenal = resumen ? Number(resumen.saldo || 0) / 2 : 0;

  function PDFTabla() {
    if (!resumen) return;
    const doc = new jsPDF();
    doc.setFontSize(13);
    doc.text(`Control de viáticos — ${nombreColab}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Periodo ${periodo}`, 14, 26);

    doc.autoTable({
      head: [["Concepto", "Importe"]],
      body: [
        ["Saldo que venía del mes anterior", money(resumen.saldoAnterior)],
        ["Entregado en el mes", money(resumen.entregado)],
        ["Comprobado en el mes", money(resumen.comprobado)],
        ["Diferencia del mes (entregado − comprobado)", money(resumen.diferencia)],
        ["SALDO ACUMULADO", money(resumen.saldo)],
      ],
      startY: 32,
      theme: "grid",
      headStyles: { fillColor: ["#911c04"], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" } },
    });

    doc.autoTable({
      head: [["Fecha", "Concepto", "Comprobado"]],
      body: comprobaciones.map((c) => [c.fecha, c.concepto, money(c.importe)]),
      foot: [["", "TOTAL", money(resumen.comprobado)]],
      startY: doc.lastAutoTable.finalY + 8,
      theme: "grid",
      headStyles: { fillColor: ["#911c04"], fontSize: 9 },
      footStyles: { fillColor: ["#911c04"], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 2: { halign: "right" } },
    });

    doc.setFontSize(9);
    doc.text(
      resumen.saldo > 0
        ? `Se le dio de más ${money(resumen.saldo)}: descontar ${money(Math.abs(ajusteQuincenal))} en cada quincena del mes siguiente.`
        : resumen.saldo < 0
          ? `Gastó de más ${money(Math.abs(resumen.saldo))}: reponer ${money(Math.abs(ajusteQuincenal))} en cada quincena del mes siguiente.`
          : "El mes cierra en ceros.",
      14,
      doc.lastAutoTable.finalY + 10
    );

    abrePDF(doc, `Viaticos-${nombreColab}-${periodo}.pdf`);
  }

  function excel() {
    if (!resumen) return;
    const data = (resumen.historial || []).map((h) => ({
      Periodo: h.periodo,
      SaldoAnterior: h.saldoAnterior,
      Entregado: h.entregado,
      Comprobado: h.comprobado,
      Diferencia: h.diferencia,
      Saldo: h.saldo,
    }));
    const columnas = [
      "Periodo", "SaldoAnterior", "Entregado", "Comprobado", "Diferencia", "Saldo",
    ];
    new ExportJsonExcel({
      fileName: `Viaticos-${nombreColab || "vendedor"}`,
      datas: [
        {
          sheetData: data,
          sheetName: "Viaticos",
          sheetFilter: columnas,
          sheetHeader: [
            "Periodo", "Saldo anterior", "Entregado", "Comprobado",
            "Diferencia", "Saldo acumulado",
          ],
        },
      ],
    }).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_admin || user.comisionesCreate ? (
        <div className="card container col-12">
          <Row>
            <Col sm={8}>
              <Button size="sm" href="/MenuAdmin" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" color="danger" onClick={PDFTabla} disabled={!resumen}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" color="primary" onClick={excel} disabled={!resumen}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Control de Viáticos</h3>

          <Row>
            <Col md={4}>
              <Label>Vendedor</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedColaborador}
                onChange={(e) => setSelectedColaborador(e.target.value)}
              >
                <option value="">Selecciona</option>
                {colaboradores
                  .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nombre} {c.apellido}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Mes</Label>
              <Input
                bsSize="sm"
                type="month"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
              />
            </Col>
          </Row>
          <br />

          {cargando ? (
            <h5 align="center">Cargando…</h5>
          ) : !resumen ? (
            <h5 align="center">Selecciona un vendedor y un mes</h5>
          ) : (
            <>
              {/* Resumen del mes */}
              <Row>
                <Col md={2}>
                  <Label>Saldo anterior</Label>
                  <Input bsSize="sm" value={money(resumen.saldoAnterior)} disabled />
                </Col>
                <Col md={2}>
                  <Label>Entregado</Label>
                  <Input bsSize="sm" value={money(resumen.entregado)} disabled />
                </Col>
                <Col md={2}>
                  <Label>Comprobado</Label>
                  <Input bsSize="sm" value={money(resumen.comprobado)} disabled />
                </Col>
                <Col md={2}>
                  <Label>Diferencia del mes</Label>
                  <Input bsSize="sm" value={money(resumen.diferencia)} disabled />
                </Col>
                <Col md={2}>
                  <Label>Saldo acumulado</Label>
                  <Input bsSize="sm" value={money(resumen.saldo)} disabled />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <br />
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 6,
                      background:
                        resumen.saldo > 0 ? "#fff3cd" : resumen.saldo < 0 ? "#d1ecf1" : "#e2e3e5",
                    }}
                  >
                    {resumen.saldo > 0 ? (
                      <>
                        Se le dio de más <b>{money(resumen.saldo)}</b>. Ajuste sugerido
                        para el mes siguiente: <b>descontar {money(Math.abs(ajusteQuincenal))}</b> en
                        cada quincena.
                      </>
                    ) : resumen.saldo < 0 ? (
                      <>
                        Gastó de más <b>{money(Math.abs(resumen.saldo))}</b>. Ajuste sugerido
                        para el mes siguiente: <b>reponer {money(Math.abs(ajusteQuincenal))}</b> en
                        cada quincena.
                      </>
                    ) : (
                      <>El mes cierra en ceros: la quincena siguiente va sin ajuste.</>
                    )}
                  </div>
                </Col>
              </Row>
              <br />

              <Row>
                {/* Entregas del mes */}
                <Col md={6}>
                  <h5>Entregado en el mes</h5>
                  <Table size="sm" striped borderless>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th align="right">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(resumen.entregas || []).map((e) => (
                        <tr key={e._id}>
                          <td>{e.fecha}</td>
                          <td>{e.tipoViaticos || "Periodo"}</td>
                          <td align="right">{money(e.viaticos)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                        <td colSpan={2} align="right">TOTAL</td>
                        <td align="right">{money(resumen.entregado)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                {/* Comprobaciones del mes */}
                <Col md={6}>
                  <h5>Comprobado en el mes</h5>
                  <Table size="sm" striped borderless>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Concepto</th>
                        <th align="right">Importe</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {comprobaciones.map((c) => (
                        <tr key={c._id}>
                          <td>{c.fecha}</td>
                          <td>{c.concepto}</td>
                          <td align="right">{money(c.importe)}</td>
                          <td>
                            <Button
                              size="sm"
                              color="danger"
                              title="Cancelar"
                              onClick={() => cancelarComprobacion(c._id)}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td>
                          <Input
                            bsSize="sm"
                            type="date"
                            value={fecha}
                            onChange={(e) => setFecha(e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="text"
                            placeholder="Concepto"
                            value={concepto}
                            onChange={(e) => setConcepto(e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            step="any"
                            placeholder="0.00"
                            value={importe}
                            onChange={(e) => setImporte(e.target.value)}
                          />
                        </td>
                        <td>
                          <Button size="sm" color="success" onClick={guardarComprobacion}>
                            <i className="fas fa-plus"></i>
                          </Button>
                        </td>
                      </tr>
                      <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                        <td colSpan={2} align="right">TOTAL</td>
                        <td align="right">{money(resumen.comprobado)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>

              {/* Historial: de aquí sale el arrastre */}
              <h5>Historial mensual</h5>
              <Table size="sm" striped borderless>
                <thead>
                  <tr>
                    <th>Periodo</th>
                    <th align="right">Saldo anterior</th>
                    <th align="right">Entregado</th>
                    <th align="right">Comprobado</th>
                    <th align="right">Diferencia</th>
                    <th align="right">Saldo acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {(resumen.historial || []).map((h) => (
                    <tr
                      key={h.periodo}
                      style={h.periodo === periodo ? { fontWeight: "bold" } : undefined}
                    >
                      <td>{h.periodo}</td>
                      <td align="right">{money(h.saldoAnterior)}</td>
                      <td align="right">{money(h.entregado)}</td>
                      <td align="right">{money(h.comprobado)}</td>
                      <td align="right">{money(h.diferencia)}</td>
                      <td align="right">{money(h.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
          <br />
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default ControlViaticos;
